import { VariantProps } from 'class-variance-authority';
import { ButtonHTMLAttributes, ReactNode, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Loading } from 'src/shared/components/ui/loaders/loading';
import { Button, buttonVariants } from 'src/shared/components/ui/buttons/button';

interface SaveButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className'> {
  children: ReactNode;
  loading: boolean;
  variant?: VariantProps<typeof buttonVariants>['variant'];
}

export default function SaveButton({ loading, children, variant = 'default', ...props }: SaveButtonProps) {
  const ref = useRef<HTMLButtonElement>(null);
  const [minWidth, setMinWidth] = useState<number | undefined>();
  const [throttledLoading, setThrottledLoading] = useState(loading);

  // calculate the width of the button to avoid layout shift when the loader renders
  useLayoutEffect(() => {
    if (ref.current && !minWidth) {
      setMinWidth(ref.current.offsetWidth);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref]);

  // throttle the loading to avoid flickering when the requests instantly resolves
  useEffect(() => {
    if (loading) {
      setThrottledLoading(true);
      setTimeout(() => setThrottledLoading(false), 500);
    }
  }, [loading]);

  // render the button first with its normal text to get the width
  if (!minWidth) {
    return (
      <Button
        className="whitespace-nowrap"
        ref={ref}
        disabled={props.disabled || loading}
        aria-disabled={props.disabled || loading}
        {...props}
        variant={variant || 'default'}
      >
        {children}
      </Button>
    );
  }

  return (
    <Button
      ref={ref}
      disabled={throttledLoading || props.disabled || loading}
      {...props}
      className="whitespace-nowrap"
      variant={variant || 'default'}
      style={{ width: minWidth ? `${minWidth}px` : '20px', whiteSpace: 'nowrap' }}
    >
      {loading || throttledLoading ? (
        <Loading color={['default', 'destructive', 'warning'].includes(variant) ? 'white' : undefined} />
      ) : (
        children
      )}
    </Button>
  );
}
